import {
  addDaysYmd,
  daysUntil,
  docStatusLabel,
  parseVmDate,
  toYmdLocal,
} from "./vmDateFilters";
import { buildDieselScheduleUpcoming, normalizeDieselRow } from "./vmOverviewStats";

const DOC_EXPIRY_FIELDS = [
  ["registrationDate", "RC"],
  ["insuranceValidUpto", "Insurance"],
  ["fitnessValidUpto", "Fitness"],
  ["taxValidUpto", "Road Tax"],
  ["pollutionValidUpto", "Pollution"],
  ["statePermitValidUpto", "State Permit"],
  ["permitUpto", "National Permit"],
  ["TemporarypermitUpto", "Temp Permit"],
];

function ymdFromValue(raw) {
  if (raw == null || raw === "") return "";
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return toYmdLocal(parseVmDate(raw));
}

function severityFromDays(daysLeft) {
  if (daysLeft < 0) return "critical";
  if (daysLeft === 0) return "critical";
  if (daysLeft <= 7) return "warning";
  if (daysLeft <= 30) return "info";
  return null;
}

function push(items, row) {
  if (!row) return;
  items.push(row);
}

function driverDisplayName(d) {
  const parts = [d.firstName, d.secondName, d.surname].filter(Boolean);
  const joined = parts.join(" ").trim();
  return joined || d.driverId || "Driver";
}

function flattenDriver(d) {
  if (!d || typeof d !== "object") return d;
  const nested = d.driverDetails && typeof d.driverDetails === "object" ? d.driverDetails : {};
  return { ...nested, ...d };
}

/** @returns {Promise<import('./fleetNotifications').FleetNotification[]>} */
export async function fetchFleetNotificationInputs(axios, apiBase) {
  const base = String(apiBase || "").replace(/\/$/, "");
  if (!base) {
    return { vehicles: [], drivers: [], dieselRows: [], maintRows: [], pendingRefills: [], pendingLeaves: [] };
  }

  const [vRes, dRes, dieselRes, maintRes, refillRes, leaveRes] = await Promise.all([
    axios.get(`${base}/onboarding/all-vehicles`).catch(() => ({ data: [] })),
    axios.get(`${base}/onboarding/drivers`).catch(() => ({ data: [] })),
    axios.get(`${base}/vehicle/diesel`, { params: { officialOnly: "1" } }).catch(() => ({ data: [] })),
    axios.get(`${base}/vehicle/truck-maintenance`, { withCredentials: true }).catch(() => ({ data: [] })),
    axios.get(`${base}/vehicle/refill-requests`, { params: { status: "PENDING" } }).catch(() => ({ data: [] })),
    axios.get(`${base}/advance/leaves`).catch(() => ({ data: [] })),
  ]);

  return {
    vehicles: Array.isArray(vRes.data) ? vRes.data : [],
    drivers: Array.isArray(dRes.data) ? dRes.data : [],
    dieselRows: (Array.isArray(dieselRes.data) ? dieselRes.data : []).map(normalizeDieselRow),
    maintRows: Array.isArray(maintRes.data) ? maintRes.data : [],
    pendingRefills: Array.isArray(refillRes.data) ? refillRes.data : [],
    pendingLeaves: (Array.isArray(leaveRes.data) ? leaveRes.data : []).filter(
      (l) => String(l.status || "").toUpperCase() === "PENDING",
    ),
  };
}

/**
 * Build alert list from vehicle + driver management data (daily relevance).
 */
export function buildFleetNotifications({
  vehicles = [],
  drivers = [],
  dieselRows = [],
  maintRows = [],
  pendingRefills = [],
  pendingLeaves = [],
}) {
  const items = [];
  const today = toYmdLocal(new Date());

  for (const v of vehicles) {
    const vn = String(v.vehicleNumber || "").trim();
    if (!vn) continue;
    const owner = v.registerName || v.ownerName || "";

    for (const [field, label] of DOC_EXPIRY_FIELDS) {
      const ymd = ymdFromValue(v[field]);
      if (!ymd) continue;
      const dl = daysUntil(ymd);
      const sev = severityFromDays(dl);
      if (!sev) continue;

      let title = `${label} — ${vn}`;
      let message = "";
      if (dl < 0) message = `${label} expired on ${ymd}.`;
      else if (dl === 0) message = `${label} expires today (${ymd}).`;
      else if (dl <= 7) message = `${label} expires in ${dl} day(s) (${ymd}).`;
      else message = `${label} expires in ${dl} days (${ymd}).`;

      push(items, {
        id: `doc-${vn}-${field}`,
        category: "vehicle",
        type: "doc_expiry",
        severity: sev,
        title,
        message: owner ? `${message} · ${owner}` : message,
        dateYmd: ymd,
        daysLeft: dl,
        vehicleNumber: vn,
        link: "/vehicle-documents",
      });
    }
  }

  const dieselUpcoming = buildDieselScheduleUpcoming(vehicles, dieselRows, "all", {});
  for (const u of dieselUpcoming) {
    if (u.daysLeft > 30) continue;
    const sev = u.daysLeft < 0 ? "critical" : u.daysLeft <= 7 ? "warning" : "info";
    push(items, {
      id: `diesel-${u.vehicleNumber}-${u.nextDueYmd}`,
      category: "vehicle",
      type: "diesel_due",
      severity: sev,
      title: `Diesel refill — ${u.vehicleNumber}`,
      message:
        u.daysLeft < 0
          ? `Overdue since ${u.nextDueYmd} (every ${u.intervalDays}d, last fill ${u.lastFillYmd || "—"}).`
          : u.daysLeft === 0
            ? `Scheduled refill due today (${u.nextDueYmd}).`
            : `Next refill in ${u.daysLeft} day(s) (${u.nextDueYmd}).`,
      dateYmd: u.nextDueYmd,
      daysLeft: u.daysLeft,
      vehicleNumber: u.vehicleNumber,
      link: "/diesel",
    });
  }

  for (const r of pendingRefills) {
    const vn = String(r.vehicleNumber || "").trim();
    push(items, {
      id: `refill-${r._id || vn}-${r.date}`,
      category: "vehicle",
      type: "refill_pending",
      severity: "warning",
      title: `Refill approval — ${vn || "Vehicle"}`,
      message: `Supervisor refill pending: ${r.driverName || "—"} · ${r.volume ?? "—"} L · ₹${r.totalAmount ?? r.amount ?? "—"}.`,
      dateYmd: ymdFromValue(r.date) || today,
      daysLeft: 0,
      vehicleNumber: vn,
      link: "/diesel",
    });
  }

  for (const r of maintRows) {
    const next = r.nextServiceDate;
    if (!next) continue;
    const ymd = ymdFromValue(next);
    if (!ymd) continue;
    const dl = daysUntil(ymd);
    if (dl > 30) continue;
    const sev = severityFromDays(dl);
    if (!sev) continue;
    const vn = String(r.truckNo || r.vehicleNumber || "").trim();
    push(items, {
      id: `maint-${r._id || vn}-${ymd}`,
      category: "vehicle",
      type: "maintenance_due",
      severity: sev,
      title: `Service due — ${vn || "Vehicle"}`,
      message:
        dl < 0
          ? `${r.maintenanceType || "Maintenance"} overdue (was ${ymd}).`
          : dl === 0
            ? `${r.maintenanceType || "Maintenance"} due today.`
            : `${r.maintenanceType || "Maintenance"} in ${dl} day(s) (${ymd}).`,
      dateYmd: ymd,
      daysLeft: dl,
      vehicleNumber: vn,
      link: "/truck-maintenance",
    });
  }

  for (const raw of drivers) {
    const d = flattenDriver(raw);
    const id = String(d.driverId || "").trim();
    if (!id) continue;
    const name = driverDisplayName(d);
    const dlYmd = ymdFromValue(d.dLValidTill || d.dlValidTill);
    if (dlYmd) {
      const dl = daysUntil(dlYmd);
      const sev = severityFromDays(dl);
      if (sev) {
        push(items, {
          id: `dl-${id}`,
          category: "driver",
          type: "dl_expiry",
          severity: sev,
          title: `Driving licence — ${name}`,
          message:
            dl < 0
              ? `DL expired on ${dlYmd} (${d.dlNumber || id}).`
              : dl === 0
                ? `DL expires today (${dlYmd}).`
                : `DL expires in ${dl} day(s) (${dlYmd}).`,
          dateYmd: dlYmd,
          daysLeft: dl,
          driverId: id,
          driverName: name,
          link: "/driver-onboarding",
        });
      }
    }
  }

  for (const l of pendingLeaves) {
    const id = String(l.driverId || "").trim();
    const start = ymdFromValue(l.startDate);
    push(items, {
      id: `leave-${l._id || id}`,
      category: "driver",
      type: "leave_pending",
      severity: "warning",
      title: `Leave request — ${id || "Driver"}`,
      message: `Pending approval: ${start || "—"} to ${ymdFromValue(l.endDate) || "—"} · ${l.reason || ""}`.trim(),
      dateYmd: start || today,
      daysLeft: start ? daysUntil(start) : 0,
      driverId: id,
      link: "/driver-leave-admin",
    });
  }

  const order = { critical: 0, warning: 1, info: 2 };
  items.sort((a, b) => {
    const so = (order[a.severity] ?? 9) - (order[b.severity] ?? 9);
    if (so !== 0) return so;
    return (a.daysLeft ?? 999) - (b.daysLeft ?? 999);
  });

  return items;
}

export function filterNotifications(items, { tab = "all", urgency = "all", search = "" }) {
  let list = items;
  if (tab === "vehicle") list = list.filter((n) => n.category === "vehicle");
  if (tab === "driver") list = list.filter((n) => n.category === "driver");

  if (urgency === "today") {
    list = list.filter((n) => n.daysLeft === 0 || (n.dateYmd && n.dateYmd === toYmdLocal(new Date())));
  } else if (urgency === "overdue") {
    list = list.filter((n) => n.daysLeft < 0);
  } else if (urgency === "expiring") {
    list = list.filter((n) => n.daysLeft >= 0 && n.daysLeft <= 7);
  } else if (urgency === "pending") {
    list = list.filter((n) => n.type === "refill_pending" || n.type === "leave_pending");
  }

  const q = search.trim().toLowerCase();
  if (q) {
    list = list.filter((n) => {
      const hay = [
        n.title,
        n.message,
        n.vehicleNumber,
        n.driverId,
        n.driverName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }

  return list;
}

export function notificationBadgeCount(items) {
  return items.filter((n) => n.severity === "critical" || n.severity === "warning").length;
}
